import { z } from "zod";

import {
  documentGenerationRequestSchema,
  type LegalConfirmationPayload,
} from "@/api/schemas";

/**
 * Form schema for Step 2 (Podaci o projektu). Extends the
 * ``DocumentGenerationRequest`` Zod schema with looser numeric coercion
 * (HTML inputs hand back strings) and per-field confidence stamps that
 * the dropzone pre-fill flow uses to flag low-trust auto-extracted
 * values.
 */

const numFromString = z.preprocess((v) => {
  if (v === "" || v == null) return undefined;
  if (typeof v === "string") {
    const parsed = parseFloat(v.replace(",", "."));
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return v;
}, z.number().optional());

const intFromString = z.preprocess((v) => {
  if (v === "" || v == null) return undefined;
  if (typeof v === "string") {
    const parsed = parseInt(v, 10);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return v;
}, z.number().int().optional());

export const projectDataSchema = z.object({
  // ---- Project identity --------------------------------------------------
  project_number: z.string().min(1, "Obavezno"),
  project_date: z.string().min(1, "Obavezno"),
  project_location: z.string().min(1, "Obavezno"),

  // ---- Investitor --------------------------------------------------------
  // The scalar fields are the PRIMARY investitor (back-compat + single-
  // investitor render). ``investitori`` (R5) carries additional ones; the
  // backend collapses a single investitor to the scalars so existing
  // projects render byte-identical.
  investitor_name: z.string().min(1, "Obavezno"),
  investitor_address: z.string().min(1, "Obavezno"),
  investitor_oib: z
    .string()
    .min(1, "Obavezno")
    .regex(/^[0-9]{11}$/, "OIB mora imati 11 znamenaka"),
  // R5 — additional investitori (beyond the primary scalars above).
  investitori: z
    .array(
      z.object({
        name: z.string().default(""),
        address: z.string().default(""),
        oib: z.string().default(""),
      }),
    )
    .default([]),
  // R6 — co-authors {ime, titula}, optional.
  suradnici: z
    .array(
      z.object({ ime: z.string().default(""), titula: z.string().default("") }),
    )
    .default([]),
  // R7 — newly-formed parcel; null = existing k.č. (today's default).
  parcelacija: z
    .object({
      novi_broj: z.string().default(""),
      maticne: z.array(z.string()).default([]),
      ko: z.string().default(""),
    })
    .nullable()
    .default(null),
  // R8 — mape (project folders); empty → no Popis mapa / Sadržaj docs.
  mape: z
    .array(
      z.object({
        redni_broj: z.string().default(""),
        struka: z.string().default(""),
        oznaka_projekta: z.string().default(""),
        izradivac: z
          .object({ tvrtka: z.string().default(""), adresa: z.string().default("") })
          .default({ tvrtka: "", adresa: "" }),
        projektant: z
          .object({
            ime: z.string().default(""),
            titula: z.string().default(""),
            broj_ovlastenja: z.string().default(""),
          })
          .default({ ime: "", titula: "", broj_ovlastenja: "" }),
      }),
    )
    .default([]),
  zajednicka_oznaka: z.string().default(""),
  // S1 — generalije: faza izrade + strukovna odrednica. Defaults match the
  // overwhelmingly common case (glavni arhitektonski projekt); free text
  // because the corpus shows real variation ("Ispravak 1", IP, IR…).
  faza: z.string().default("Glavni projekt"),
  strukovna_odrednica: z.string().default("Arhitektonski projekt"),

  // ---- Ured / projektant -------------------------------------------------
  ured_name: z.string().min(1, "Obavezno"),
  ured_address: z.string().min(1, "Obavezno"),
  glavni_projektant_name: z.string().min(1, "Obavezno"),
  glavni_projektant_title: z.string().min(1),
  glavni_projektant_license: z.string().min(1),
  direktor_name: z.string().min(1, "Obavezno"),

  // ---- Katastar / lokacija ----------------------------------------------
  katastarska_cestica: z.string().min(1, "Obavezno"),
  katastarski_opcina: z.string().min(1, "Obavezno"),
  cestica_povrsina_m2: numFromString.refine((v) => v != null, "Obavezno"),

  // ---- Vrsta građevine ---------------------------------------------------
  building_type: z.string().min(1, "Obavezno"),
  building_subtype: z.string().nullable().optional(),
  work_type: z.string().default("novogradnja"),
  zahvat_opis: z.string().default(""),

  // ---- Geometrija --------------------------------------------------------
  katnost: z.string().default(""),
  bruto_areas: z.record(numFromString).default({}),
  tlocrtna_projekcija_m2: numFromString.default(0),
  building_length_m: numFromString.default(0),
  building_width_m: numFromString.default(0),
  building_height_v_m: numFromString.default(0),
  building_height_V_m: numFromString.default(0),

  // ---- Brojevi -----------------------------------------------------------
  num_stambenih: intFromString.default(0),
  num_poslovnih: intFromString.default(0),
  num_parking: intFromString.default(0),
  zelena_povrsina_m2: numFromString.default(0),

  // ---- Udaljenosti -------------------------------------------------------
  udaljenost_sjever_m: numFromString.nullable().optional(),
  udaljenost_jug_m: numFromString.nullable().optional(),
  udaljenost_istok_m: numFromString.nullable().optional(),
  udaljenost_zapad_m: numFromString.nullable().optional(),
  regulacijski_pravac_m: numFromString.nullable().optional(),

  // ---- Context (Step 1 carry-through) ------------------------------------
  context: z.string().nullable().optional(),
  designation: z.string().nullable().optional(),
  in_city_center: z.boolean().default(false),
  // Carried forward from the feasibility step (Step 1) so docgen reads
  // the same spatial plan the user just analysed against. "" = unknown:
  // the backend then tries the k.o.→JLS resolver and otherwise renders
  // the "plan not in database" placeholder (R1) — it must NEVER silently
  // default to another municipality's plan.
  municipality_code: z.string().default(""),

  // ---- IFC depth (opaque pass-through) -----------------------------------
  openings: z.array(z.record(z.unknown())).default([]),
  materials: z.array(z.record(z.unknown())).default([]),
  rooms_with_polygons: z.array(z.record(z.unknown())).default([]),
  building_footprint_polygon: z.array(z.unknown()).default([]),
  extraction_warnings: z.array(z.string()).default([]),
});

/**
 * ``ProjectDataInput`` is the loose, "what the form actually holds"
 * shape (numbers may be empty strings before the user types). The
 * resolver-validated, parsed shape is ``ProjectDataValues``.
 */
export type ProjectDataInput = z.input<typeof projectDataSchema>;
export type ProjectDataValues = z.output<typeof projectDataSchema>;

/**
 * Canonical list of numeric form fields. Used by the NL-prefill numeric
 * confidence cap (``capNumericConfidenceForNL``) — shared between the
 * in-form prefill path (``ProjectDataForm``) and the off-form draft
 * prefill path (``prefillToDraft``) so the trust discount is identical
 * no matter where extraction lands. Must mirror the ``number``-typed
 * entries in ``FIELD_META`` (ProjectDataForm).
 */
export const NUMERIC_FIELD_NAMES = [
  "cestica_povrsina_m2",
  "tlocrtna_projekcija_m2",
  "building_length_m",
  "building_width_m",
  "building_height_v_m",
  "building_height_V_m",
  "num_stambenih",
  "num_poslovnih",
  "num_parking",
  "zelena_povrsina_m2",
  "udaljenost_sjever_m",
  "udaljenost_jug_m",
  "udaljenost_istok_m",
  "udaljenost_zapad_m",
  "regulacijski_pravac_m",
] as const;

export function toGenerateRequest(
  values: ProjectDataValues,
  legalConfirmation?: LegalConfirmationPayload | null,
  intake?: {
    situacija?: Record<string, unknown> | null;
    recognized_docs?: Array<Record<string, unknown>>;
    special_condition_bodies?: string[] | null;
    // B1 — vision-over-nacrti pass-through.
    nacrt_opis?: Array<Record<string, unknown>>;
    nacrt_confirmed_facts?: Array<Record<string, unknown>>;
  } | null,
  office?: Record<string, unknown> | null,
  spranca?: string | Record<string, unknown> | null,
) {
  // ``documentGenerationRequestSchema`` is the source of truth; we run
  // the values through it so any default coercion (e.g. ``katnost``
  // empty string → "") matches the backend's Pydantic shape exactly.
  // ``intake`` carries what Step-2 panels collected beyond the form
  // (situacija report, recognized opći-dio documents) so the backend
  // builds the same canonical ProjectModel the completeness panel showed.
  return documentGenerationRequestSchema.parse({
    ...values,
    cestica_povrsina_m2: values.cestica_povrsina_m2 ?? 0,
    tlocrtna_projekcija_m2: values.tlocrtna_projekcija_m2 ?? 0,
    building_length_m: values.building_length_m ?? 0,
    building_width_m: values.building_width_m ?? 0,
    building_height_v_m: values.building_height_v_m ?? 0,
    building_height_V_m: values.building_height_V_m ?? 0,
    zelena_povrsina_m2: values.zelena_povrsina_m2 ?? 0,
    legal_confirmation: legalConfirmation ?? null,
    situacija: intake?.situacija ?? null,
    recognized_docs: intake?.recognized_docs ?? [],
    special_condition_bodies: intake?.special_condition_bodies ?? null,
    // B1 — vision-over-nacrti; empty → docgen byte-identical to today.
    nacrt_opis: intake?.nacrt_opis ?? [],
    nacrt_confirmed_facts: intake?.nacrt_confirmed_facts ?? [],
    office: office ?? null,
    // S3 — aktivna špranca aktivira klase-B sekcije i redoslijed poglavlja.
    spranca: spranca ?? null,
  });
}

export const DEFAULT_PROJECT_DATA: ProjectDataInput = {
  project_number: "",
  project_date: new Date().toISOString().slice(0, 10),
  project_location: "",
  investitor_name: "",
  investitor_address: "",
  investitor_oib: "",
  investitori: [],
  suradnici: [],
  parcelacija: null,
  mape: [],
  zajednicka_oznaka: "",
  faza: "Glavni projekt",
  strukovna_odrednica: "Arhitektonski projekt",
  ured_name: "",
  ured_address: "",
  glavni_projektant_name: "",
  glavni_projektant_title: "ovl. arh.",
  glavni_projektant_license: "",
  direktor_name: "",
  katastarska_cestica: "",
  katastarski_opcina: "",
  cestica_povrsina_m2: undefined,
  building_type: "obiteljska_kuca",
  building_subtype: "",
  work_type: "novogradnja",
  zahvat_opis: "",
  // Empty by default — a 2D DXF carries no storeys, so seeding "Po+P+1"
  // here made an unprovided katnost look like real data downstream (B1).
  // The "Po+P+1" hint stays as the field placeholder, not as a value.
  katnost: "",
  bruto_areas: {},
  // Empty by default (not 0) so the form shows a blank field for a value the
  // architect hasn't provided — a fabricated "0 m²"/"0 mjesta" is exactly what
  // B1 kills. The backend fact sheet also treats a 0 here as "not provided".
  tlocrtna_projekcija_m2: undefined,
  building_length_m: undefined,
  building_width_m: undefined,
  building_height_v_m: undefined,
  building_height_V_m: undefined,
  num_stambenih: undefined,
  num_poslovnih: undefined,
  num_parking: undefined,
  zelena_povrsina_m2: undefined,
  udaljenost_sjever_m: null,
  udaljenost_jug_m: null,
  udaljenost_istok_m: null,
  udaljenost_zapad_m: null,
  regulacijski_pravac_m: null,
  context: null,
  designation: null,
  in_city_center: false,
  municipality_code: "",
  openings: [],
  materials: [],
  rooms_with_polygons: [],
  building_footprint_polygon: [],
  extraction_warnings: [],
};
